import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationInfo, supabaseLocationService } from '../../services/supabaseLocationService';
import { supabaseClient } from '../../services/supabase';
import { LocationSelector } from '../LocationSelector';
import { HeroSlideshow } from '../layout/HeroSlideshow';
import { PageHeader } from '../layout/PageHeader';
import { useLocation } from '../../contexts/LocationContext';
import { BurdenBenefitsChart } from '../charts/BurdenBenefitsChart';
import { fetchElectricitySeries, fetchNaturalGasSeries } from '../charts/EnergyUseSupabase';
import { fetchGHGInventoryData } from '../charts/GHGInventorySupabase';
import { DataService } from '../../services/dataService';
import { VMTChart } from '../charts/VMTChart';
import { TransportationModeShare } from '../charts/TransportationModeShare';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import WasteTonnageChart from '../charts/WasteTonnageChart';
import LandfillEmissionsStackedChart from '../charts/LandfillEmissionsStackedChart';
import IncomeByRaceBar from '../charts/IncomeByRaceBar';
import HousingBurdenSection from '../charts/HousingBurdenSection';
import { CumulativeSolarCapacityChart } from '../charts/CumulativeSolarCapacityChart';
import { SectorBasedGHGInventory } from '../charts/SectorBasedGHGInventory';
import { ConsumptionBasedGHGInventory } from '../charts/ConsumptionBasedGHGInventory';
import { fetchConsumptionBasedGHGData } from '../../services/consumptionBasedGHGService';
import { setVMTDataFromTransportation } from '../../services/vmtDataService';
import DataDownloadModal from '../auth/DataDownloadModal';

import './Overview.css';

// Extend Window interface for Overview loading state
declare global {
  interface Window {
    overviewFullyLoaded?: boolean;
  }
}

// Function to calculate stacked data as sum of residential + commercial (same as Energy page)
const calculateStackedData = (resData: any, comData: any, type: string) => {
  if (!resData?.chartData || !comData?.chartData) return null;

  const resLabels = (resData.chartData.labels || []) as number[];
  const comLabels = (comData.chartData.labels || []) as number[];
  
  // Get years that exist in both residential and commercial data
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const allYears: number[] = Array.from(resYears).filter((year: number) => comYears.has(year)).sort();
  
  // Extract data from all datasets (observed, interpolated, extrapolated) for each sector
  const resDatasets = resData.chartData.datasets || [];
  const comDatasets = comData.chartData.datasets || [];
  
  // Create maps for each sector's total values (sum of all data types)
  const resMap = new Map<number, number>();
  const comMap = new Map<number, number>();
  
  // Sum up all data types for residential
  resLabels.forEach((year: number, i: number) => {
    let total = 0;
    resDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    resMap.set(year, total);
  });
  
  // Sum up all data types for commercial
  comLabels.forEach((year: number, i: number) => {
    let total = 0;
    comDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    comMap.set(year, total);
  });

  // Create stacked chart data
  const stackedChartData = {
    labels: allYears,
    datasets: [
      {
        label: 'Residential',
        data: allYears.map(year => resMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderWidth: 1,
        stack: 'total'
      },
      {
        label: 'Commercial',
        data: allYears.map(year => comMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderWidth: 1,
        stack: 'total'
      }
    ]
  };

  // Create chart options for stacked bar chart
  const stackedChartOptions = {
    type: 'bar',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const v = Number(ctx.parsed.y);
            return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
          }
        }
      }
    },
    scales: {
      x: { 
        stacked: true, 
        title: { display: true, text: 'Year' } 
      },
      y: { 
        stacked: true, 
        beginAtZero: true, 
        title: { 
          display: true, 
          text: type === 'Electricity' ? 'kWh' : 'Therms'
        } 
      }
    }
  };

  return {
    chartData: stackedChartData,
    chartOptions: stackedChartOptions
  };
};

interface OverviewProps {
  onLocationSelect: (location: string) => void;
  isLandingPage?: boolean;
}

export const Overview: React.FC<OverviewProps> = ({ onLocationSelect, isLandingPage = false }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const navigate = useNavigate();

  // Helper function to handle navigation with loading check
  const handleNavigateWithCheck = (path: string) => {
    // Always allow navigation to Transportation page
    if (path === '/transportation') {
      navigate(path);
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      return;
    }
    
    // Check if Overview is fully loaded before allowing navigation to other pages
    if (!overviewFullyLoaded) {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    navigate(path);
    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };
  const [availableLocations, setAvailableLocations] = useState<LocationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptCommunications, setAcceptCommunications] = useState(true);
  const [elecStacked, setElecStacked] = useState<any>(null);
  const [gasStacked, setGasStacked] = useState<any>(null);
  const [elecChangeSince2005, setElecChangeSince2005] = useState<number | null>(null);
  const [gasChangeSince2005, setGasChangeSince2005] = useState<number | null>(null);
  const [cumulativeSolarCapacity, setCumulativeSolarCapacity] = useState<number | null>(null);
  const [ghgInventoryData, setGhgInventoryData] = useState<any>(null);
  const [consumptionGHGData, setConsumptionGHGData] = useState<any>(null);
  const [ghgChartType, setGhgChartType] = useState<'stacked-area' | 'line' | 'pie'>('stacked-area');
  const [consumptionChartType, setConsumptionChartType] = useState<'stacked-area' | 'line' | 'pie'>('stacked-area');
  const [housingBurdenPercentile, setHousingBurdenPercentile] = useState<number | null>(null);
  const [incomeDifferenceRatio, setIncomeDifferenceRatio] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDataDownload, setShowDataDownload] = useState(false);
  const [vmtChangeSince2005, setVmtChangeSince2005] = useState<number | null>(null);
  const [privateVehiclePercent, setPrivateVehiclePercent] = useState<number | null>(null);
  const [evPercent, setEvPercent] = useState<number | null>(null);
  const [wasteChangeSince2005, setWasteChangeSince2005] = useState<number | null>(null);
  const [landfillEmissions2023, setLandfillEmissions2023] = useState<number | null>(null);
  const [burdenPercentile, setBurdenPercentile] = useState<number | null>(null);
  const [benefitsPercentile, setBenefitsPercentile] = useState<number | null>(null);
  const [vmtDataReady, setVmtDataReady] = useState<boolean>(false);
  const [vmtDataAvailable, setVmtDataAvailable] = useState<boolean>(false);
  const [overviewVMTChartLoaded, setOverviewVMTChartLoaded] = useState<boolean>(false);
  const [overviewFullyLoaded, setOverviewFullyLoaded] = useState<boolean>(false);
  const ghgInventoryLoadingRef = useRef<boolean>(false); // Prevent multiple simultaneous GHG Inventory loads
  const ghgInventoryLoadedForLocationRef = useRef<string | null>(null); // Track which location has GHG Inventory loaded
  const vmtChartDataLoadedRef = useRef<boolean>(false); // Track if VMT chart has loaded data for current location

  const renderSampleTitle = (title: React.ReactNode, includeSample = true) => (
    <span>
      {includeSample && <span style={{ fontWeight: 400 }}>Sample of </span>}
      <span style={{ fontWeight: 700 }}>{title}</span>
      {includeSample && <span style={{ fontWeight: 400 }}> Figures.</span>}
    </span>
  );

  // Note: GHG Inventory loading is now handled by the Overview VMT chart's onDataCalculated callback
  // This ensures we wait for the VMT chart to finish loading before loading GHG Inventory

  // Track when Overview page is fully loaded (VMT + GHG Inventory) and set global flag
  useEffect(() => {
    if (isLandingPage || !selectedLocation) {
      // Reset flag when on landing page or no location selected
      setOverviewFullyLoaded(false);
      if (typeof window !== 'undefined') {
        window.overviewFullyLoaded = false;
      }
      return;
    }

    // Overview is fully loaded when VMT chart has loaded AND GHG inventory has loaded
    // Check both the state flag and the ref, and also check if ghgInventoryData exists
    const isFullyLoaded = overviewVMTChartLoaded && 
                          ghgInventoryLoadedForLocationRef.current === selectedLocation &&
                          ghgInventoryData !== null;
    setOverviewFullyLoaded(isFullyLoaded);
    
    // Set global flag for Navigation component to check
    if (typeof window !== 'undefined') {
      window.overviewFullyLoaded = isFullyLoaded;
    }
    
    if (isFullyLoaded) {
      console.log('✅ Overview: Page fully loaded - VMT and GHG Inventory ready');
      console.log('✅ Overview: overviewVMTChartLoaded:', overviewVMTChartLoaded);
      console.log('✅ Overview: ghgInventoryLoadedForLocationRef:', ghgInventoryLoadedForLocationRef.current);
      console.log('✅ Overview: ghgInventoryData exists:', ghgInventoryData !== null);
    } else {
      console.log('⏳ Overview: Still loading - VMT:', overviewVMTChartLoaded, 'GHG:', ghgInventoryLoadedForLocationRef.current === selectedLocation, 'GHG Data:', ghgInventoryData !== null);
    }
  }, [overviewVMTChartLoaded, selectedLocation, isLandingPage, ghgInventoryData]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        await supabaseLocationService.initialize();
        const locations = supabaseLocationService.getAvailableLocations();
        setAvailableLocations(locations);
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  // Handle scroll detection for sticky location selector
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Preload data in background when location is selected on Overview dashboard
  useEffect(() => {
    if (isLandingPage || !selectedLocation || !selectedGeofips) {
        // Clear data when location is not available
        setGhgInventoryData(null);
        setVmtDataReady(false);
        setVmtDataAvailable(false);
        setOverviewVMTChartLoaded(false);
        setOverviewFullyLoaded(false);
        ghgInventoryLoadingRef.current = false; // Reset loading flag
        ghgInventoryLoadedForLocationRef.current = null; // Reset location tracking
        vmtChartDataLoadedRef.current = false; // Reset VMT chart loaded flag
        if (typeof window !== 'undefined') {
          window.overviewFullyLoaded = false;
        }
      return;
    }

    console.log('🔄 Overview: Starting background data preload for', selectedLocation);

    // Preload data in sequence: Load VMT first (from Transportation page if available, otherwise load it here), then GHG inventory, then other charts
    const preloadData = async () => {
      try {
        // Clear GHG Inventory data and VMT flags when starting preload for new location
        setGhgInventoryData(null);
        setVmtDataReady(false);
        setVmtDataAvailable(false);
        setOverviewVMTChartLoaded(false);
        setOverviewFullyLoaded(false);
        ghgInventoryLoadingRef.current = false; // Reset loading flag
        ghgInventoryLoadedForLocationRef.current = null; // Reset location tracking
        vmtChartDataLoadedRef.current = false; // Reset VMT chart loaded flag
        if (typeof window !== 'undefined') {
          window.overviewFullyLoaded = false;
        }
        
        // 1. Load VMT data FIRST (required for sector-based GHG inventory calculation)
        // Priority: Use VMT from Transportation page if available, otherwise load it here
        console.log('🔄 Overview: Checking for VMT data...');
        const { isVMTDataAvailable, setVMTDataFromTransportation } = await import('../../services/vmtDataService');
        
        // Check if VMT data is already available (from Transportation page)
        let vmtAvailable = isVMTDataAvailable();
        setVmtDataAvailable(vmtAvailable);
        
        if (!vmtAvailable) {
          // Wait for VMT data from Transportation page (poll every 500ms, max 15 seconds)
          // This gives Transportation page time to load VMT in the background
          console.log('⏳ Overview: VMT data not yet available, waiting for Transportation page to load VMT...');
          let attempts = 0;
          const maxAttempts = 30; // 15 seconds max wait
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              attempts++;
              vmtAvailable = isVMTDataAvailable();
              setVmtDataAvailable(vmtAvailable);
              if (vmtAvailable) {
                console.log('✅ Overview: VMT data now available from Transportation page');
                clearInterval(checkInterval);
                setVmtDataReady(true);
                resolve();
              } else if (attempts >= maxAttempts) {
                console.log('⚠️ Overview: VMT data not available from Transportation page after waiting, loading VMT data here...');
                clearInterval(checkInterval);
                resolve();
              }
            }, 500);
          });
        } else {
          console.log('✅ Overview: VMT data already available from Transportation page');
          setVmtDataReady(true);
        }

        // If VMT still not available, load it here (same method as Transportation page)
        if (!vmtAvailable) {
          console.log('🔄 Overview: Loading VMT data (Transportation page not visited yet)...');
          try {
            // Fetch all three data sources like VMTChart does
            const [rawVmtDataResult, backcastedVmtDataResult, emfacDataResult] = await Promise.all([
              DataService.getVMTData(selectedLocation, undefined, selectedGeofips).catch(err => {
                console.warn('VMT raw data fetch failed:', err);
                return [];
              }),
              DataService.getVMTDataWithScaling(selectedGeofips, 'Combined', '2025', '2025').catch(err => {
                console.warn('VMT backcasted data fetch failed:', err);
                return [];
              }),
              DataService.getEMFACBackcastingDataByGeofips(selectedGeofips, [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], 'Combined').catch(err => {
                console.warn('EMFAC data not available, continuing without it:', err);
                return [];
              })
            ]);
            
            // Filter to only include PRIVATE_AUTO and ON_DEMAND modes (like VMTChart)
            const rawPrivateAuto = rawVmtDataResult.filter((d: any) => d.mode === 'PRIVATE_AUTO');
            const rawOnDemand = rawVmtDataResult.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
            const backcastedPrivateAuto = backcastedVmtDataResult.filter((d: any) => d.mode === 'PRIVATE_AUTO');
            const backcastedOnDemand = backcastedVmtDataResult.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
            
            // Get 2015 VMT data as the baseline for EMFAC backcasting
            const vmt2015PrivateAuto = backcastedPrivateAuto.find((d: any) => d.year === 2015);
            const vmt2015OnDemand = backcastedOnDemand.find((d: any) => d.year === 2015);
            const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);
            
            // Process EMFAC backcasting for 2005-2014 (same logic as VMTChart)
            const emfacBackcastedData: any[] = [];
            const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];
            
            if (vmt2015Total > 0 && emfacDataResult.length > 0) {
              // Sort ratios by year descending (2014, 2013, ..., 2005)
              const sortedRatios = emfacDataResult
                .filter((r: any) => emfacYears.includes(r.year))
                .sort((a: any, b: any) => b.year - a.year);
              
              // Calculate VMT for each year sequentially
              const vmtByYear = new Map<number, number>();
              vmtByYear.set(2015, vmt2015Total);
              
              // Apply ratios sequentially: year Y = year Y+1 * ratio_Y
              for (const emfacRecord of sortedRatios) {
                const nextYear = emfacRecord.year + 1;
                const nextYearVMT = vmtByYear.get(nextYear);
                
                if (nextYearVMT && emfacRecord.ratio) {
                  const currentYearVMT = nextYearVMT * emfacRecord.ratio;
                  vmtByYear.set(emfacRecord.year, currentYearVMT);
                }
              }
              
              // Create data points for each year, distributing between PRIVATE_AUTO and ON_DEMAND
              const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8;
              const onDemandRatio = 1 - privateAutoRatio;
              
              for (const year of emfacYears) {
                const yearVMT = vmtByYear.get(year);
                if (yearVMT !== undefined && yearVMT > 0) {
                  emfacBackcastedData.push({
                    year: year,
                    mode: 'PRIVATE_AUTO',
                    vmt: yearVMT * privateAutoRatio
                  });
                  emfacBackcastedData.push({
                    year: year,
                    mode: 'ON_DEMAND_AUTO',
                    vmt: yearVMT * onDemandRatio
                  });
                }
              }
            }
            
            // Combine all years: EMFAC (2005-2014) + AADT backcasted (2015-2022) + Raw (2023)
            const allYears = Array.from(new Set([
              ...emfacBackcastedData.map((d: any) => d.year),
              ...backcastedPrivateAuto.map((d: any) => d.year),
              ...backcastedOnDemand.map((d: any) => d.year),
              ...rawPrivateAuto.map((d: any) => d.year),
              ...rawOnDemand.map((d: any) => d.year)
            ])).sort();
            
            // Create combined VMT data for all years
            const combinedVmtData = allYears.map(year => {
              if (year === 2023) {
                // Use Replica data for 2023
                const privateAutoVmt = rawPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
                const onDemandVmt = rawOnDemand.find((d: any) => d.year === year)?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              } else if (year >= 2005 && year <= 2014) {
                // Use EMFAC backcasted data for 2005-2014
                const privateAutoVmt = emfacBackcastedData.find((d: any) => d.year === year && d.mode === 'PRIVATE_AUTO')?.vmt || 0;
                const onDemandVmt = emfacBackcastedData.find((d: any) => d.year === year && d.mode === 'ON_DEMAND_AUTO')?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              } else {
                // Use AADT backcasted data for 2015-2022
                const privateAutoVmt = backcastedPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
                const onDemandVmt = backcastedOnDemand.find((d: any) => d.year === year)?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              }
            });
            
            if (combinedVmtData.length > 0) {
              const vmtData = combinedVmtData.map((d: any) => ({
                year: d.year,
                vmt: d.vmt || 0,
                source: 'overview_preload',
                method_note: 'Preloaded from Overview page (Transportation page not visited)'
              }));
              
              // Set VMT data in the service FIRST
              setVMTDataFromTransportation(vmtData);
              
              // Also set in window variable (legacy, but GHG Inventory checks this first)
              if (typeof window !== 'undefined') {
                window.vmtChartData = vmtData.map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0
                }));
              }
              
              // Verify VMT data is now available before proceeding
              const { isVMTDataAvailable: checkVMT } = await import('../../services/vmtDataService');
              const vmtNowAvailable = checkVMT();
              
              if (vmtNowAvailable) {
                console.log('✅ Overview: VMT data loaded and verified in service:', vmtData.length, 'records');
              console.log('✅ Overview: VMT years included:', vmtData.map((d: any) => d.year).sort());
              vmtAvailable = true;
                setVmtDataAvailable(true);
              setVmtDataReady(true);
              } else {
                console.error('❌ Overview: VMT data was set but isVMTDataAvailable() returned false');
                vmtAvailable = false;
                setVmtDataAvailable(false);
                setVmtDataReady(false);
              }
            } else if (rawVmtDataResult.length > 0) {
              // Fallback to raw data if backcasted data is not available
              const vmtData = rawVmtDataResult
                .filter((d: any) => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
                .reduce((acc: any, d: any) => {
                  const existing = acc.find((item: any) => item.year === d.year);
                  if (existing) {
                    existing.vmt += (d.vmt || 0);
                  } else {
                    acc.push({ year: d.year, vmt: d.vmt || 0 });
                  }
                  return acc;
                }, [])
                .map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0,
                  source: 'overview_preload_raw',
                  method_note: 'Preloaded from Overview page (raw data, Transportation page not visited)'
                }));
              
              setVMTDataFromTransportation(vmtData);
              
              if (typeof window !== 'undefined') {
                window.vmtChartData = vmtData.map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0
                }));
              }
              
              // Verify VMT data is now available before proceeding
              const { isVMTDataAvailable: checkVMT } = await import('../../services/vmtDataService');
              const vmtNowAvailable = checkVMT();
              
              if (vmtNowAvailable) {
                console.log('✅ Overview: VMT data loaded (raw) and verified:', vmtData.length, 'records');
              vmtAvailable = true;
                setVmtDataAvailable(true);
              setVmtDataReady(true);
              } else {
                console.error('❌ Overview: VMT data was set but isVMTDataAvailable() returned false');
                vmtAvailable = false;
                setVmtDataAvailable(false);
                setVmtDataReady(false);
              }
            }
          } catch (error) {
            console.warn('Overview: VMT load failed:', error);
          }
        } else {
          // VMT was already available from Transportation page
          setVmtDataAvailable(true);
          setVmtDataReady(true);
        }

        // 2. Wait for Overview page VMT chart to load before loading GHG Inventory
        // The VMT chart will load data from Transportation page if available
        // We need to wait for the chart to finish loading before proceeding
        console.log('⏳ Overview: Waiting for Overview page VMT chart to load...');
        
        // Don't preload GHG Inventory here - wait for VMT chart onDataCalculated callback

        // 3-7. Load all independent data in parallel (don't depend on VMT or each other)
        console.log('🔄 Overview: Loading independent data in parallel...');
        await Promise.all([
          // 3. Consumption-based GHG data (independent of VMT)
          fetchConsumptionBasedGHGData(selectedLocation)
            .then((consumptionResult) => {
          setConsumptionGHGData(consumptionResult);
          console.log('✅ Overview: Consumption-based GHG data preloaded');
            })
            .catch((error) => {
          console.warn('Overview: Consumption-based GHG preload failed:', error);
            }),

          // 4. Housing Burden percentile (independent of VMT)
          (async () => {
        try {
          // Get latest year's renter percentile (35%+ burden)
          const renterResult = await supabaseClient
            .from('housing_burden_renter')
            .select('year, percentile')
            .ilike('location_name', `%${selectedLocation}%`)
            .eq('housing_burden', '35.0 percent or more')
            .not('percentile', 'is', null)
            .gt('percentile', 0)
            .order('year', { ascending: false })
            .limit(1);
          
          if (renterResult.data && renterResult.data.length > 0 && renterResult.data[0].percentile) {
            const percentile = renterResult.data[0].percentile;
            const percentile100 = percentile <= 1 ? percentile * 100 : percentile;
            setHousingBurdenPercentile(percentile100);
            console.log('✅ Overview: Housing Burden percentile loaded:', percentile100);
          } else {
            // Try owner data as fallback
            const ownerResult = await supabaseClient
              .from('owner_housing_burden')
              .select('year, percentile')
              .ilike('location_name', `%${selectedLocation}%`)
              .eq('owner_housing_burden_level', '35.0 percent or more')
              .eq('mortgage_status', 'All')
              .not('percentile', 'is', null)
              .gt('percentile', 0)
              .order('year', { ascending: false })
              .limit(1);
            
            if (ownerResult.data && ownerResult.data.length > 0 && ownerResult.data[0].percentile) {
              const percentile = ownerResult.data[0].percentile;
              const percentile100 = percentile <= 1 ? percentile * 100 : percentile;
              setHousingBurdenPercentile(percentile100);
              console.log('✅ Overview: Housing Burden percentile loaded from owner data:', percentile100);
            }
          }
        } catch (error) {
          console.warn('Overview: Housing Burden percentile preload failed:', error);
        }
          })(),

          // 5-6. Electricity and Natural Gas data (load once, use for both)
          (async () => {
            try {
              const [elecRes, elecCom, gasRes, gasCom] = await Promise.all([
                fetchElectricitySeries(selectedLocation, 'residential').catch(() => null),
                fetchElectricitySeries(selectedLocation, 'commercial').catch(() => null),
                fetchNaturalGasSeries(selectedLocation, 'residential').catch(() => null),
                fetchNaturalGasSeries(selectedLocation, 'commercial').catch(() => null)
              ]);
              
              // Process Electricity data
              if (elecRes && elecCom) {
                const elecStackedData = calculateStackedData(elecRes, elecCom, 'Electricity');
                if (elecStackedData) {
                  setElecStacked(elecStackedData);
                  // Calculate percent change since 2005
                  const labels = elecStackedData.chartData.labels as number[];
                  const datasets = elecStackedData.chartData.datasets;
                  if (labels.length > 0 && datasets.length > 0) {
                    const year2005Index = labels.indexOf(2005);
                    const latestYearIndex = labels.length - 1;
                    if (year2005Index >= 0 && latestYearIndex >= 0) {
                      const total2005 = datasets.reduce((sum: number, ds: any) => sum + (ds.data[year2005Index] || 0), 0);
                      const totalLatest = datasets.reduce((sum: number, ds: any) => sum + (ds.data[latestYearIndex] || 0), 0);
                      if (total2005 > 0) {
                        const changePercent = ((totalLatest - total2005) / total2005) * 100;
                        setElecChangeSince2005(changePercent);
                      } else {
                        setElecChangeSince2005(null);
                      }
                    }
                  }
                }
              }
              
              // Process Natural Gas data
          if (gasRes && gasCom) {
            const gasStackedData = calculateStackedData(gasRes, gasCom, 'Natural Gas');
            if (gasStackedData) {
              setGasStacked(gasStackedData);
                  // Calculate percent change since 2005
                  const labels = gasStackedData.chartData.labels as number[];
                  const datasets = gasStackedData.chartData.datasets;
                  if (labels.length > 0 && datasets.length > 0) {
                    const year2005Index = labels.indexOf(2005);
                    const latestYearIndex = labels.length - 1;
                    if (year2005Index >= 0 && latestYearIndex >= 0) {
                      const total2005 = datasets.reduce((sum: number, ds: any) => sum + (ds.data[year2005Index] || 0), 0);
                      const totalLatest = datasets.reduce((sum: number, ds: any) => sum + (ds.data[latestYearIndex] || 0), 0);
                      if (total2005 > 0) {
                        const changePercent = ((totalLatest - total2005) / total2005) * 100;
                        setGasChangeSince2005(changePercent);
                      } else {
                        setGasChangeSince2005(null);
                      }
                    }
                  }
                }
              }
              
              console.log('✅ Overview: Electricity and Natural Gas data preloaded');
        } catch (error) {
              console.warn('Overview: Electricity/Natural Gas preload failed:', error);
            }
          })(),

          // 7. Solar PV data - calculate cumulative capacity (independent of VMT)
          (async () => {
            try {
              if (selectedGeofips) {
                // Fetch solar data to calculate cumulative capacity
                const { data: solarData } = await supabaseClient
                  .from('all_pv_size')
                  .select('year, sector, total_watts')
                  .eq('geoid', selectedGeofips)
                  .lte('year', 2024)
                  .order('year', { ascending: true });
                
                if (solarData && solarData.length > 0) {
                  // Calculate cumulative capacity by summing all installations across all years
                  // This matches how CumulativeSolarCapacityChart calculates it
                  const yearSectorMap = new Map<number, Map<string, number>>();
                  solarData.forEach((record: any) => {
                    const year = parseInt(record.year.toString());
                    if (year > 2024) return;
                    
                    if (!yearSectorMap.has(year)) {
                      yearSectorMap.set(year, new Map());
                    }
                    const sectorMap = yearSectorMap.get(year)!;
                    const sector = record.sector || 'unknown';
                    const capacity = (parseFloat(record.total_watts.toString()) || 0) / 1000; // Convert to kW
                    sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacity);
                  });
                  
                  // Calculate cumulative capacity (sum all sectors across all years)
                  const allSectors = new Set<string>();
                  yearSectorMap.forEach(sectorMap => {
                    sectorMap.forEach((_, sector) => allSectors.add(sector));
                  });
                  
                  const sortedYears = Array.from(yearSectorMap.keys()).sort();
                  const sectorCumulativeMap = new Map<string, number>();
                  allSectors.forEach(sector => sectorCumulativeMap.set(sector, 0));
                  
                  sortedYears.forEach(year => {
                    const yearSectorMap_data = yearSectorMap.get(year) || new Map();
                    allSectors.forEach(sector => {
                      const sectorCapacity = yearSectorMap_data.get(sector) || 0;
                      const cumulativeSectorCapacity = sectorCumulativeMap.get(sector)! + sectorCapacity;
                      sectorCumulativeMap.set(sector, cumulativeSectorCapacity);
                    });
                  });
                  
                  const cumulativeCapacityKW = Array.from(sectorCumulativeMap.values()).reduce((sum, val) => sum + val, 0);
                  const cumulativeMW = cumulativeCapacityKW / 1000; // Convert kW to MW
                  setCumulativeSolarCapacity(cumulativeMW);
                }
              }
          console.log('✅ Overview: Solar PV data preloaded');
        } catch (error) {
          console.warn('Overview: Solar PV preload failed:', error);
        }
          })()
        ]);

        console.log('✅ Overview: Background data preload completed');
      } catch (error) {
        console.error('Overview: Error during background preload:', error);
      }
    };

    // Start preloading in background (don't await)
    preloadData();
  }, [selectedLocation, selectedGeofips, isLandingPage]);

  const cities = availableLocations.filter(loc => loc.type === 'city');
  const counties = availableLocations.filter(loc => loc.type === 'county');

  // If this is the landing page, show landing page content
  if (isLandingPage) {
    return (
      <div className="overview-page">
        {/* Hero Slideshow */}
        <HeroSlideshow />
        
        {/* Page Header - New Tools Hub Title */}
        <PageHeader 
          title="Climate Action Tools & Resources"
          subtitle="Access comprehensive climate action planning tools, calculators, and data resources for California communities"
          variant="overview"
        />

      <div className="overview-content">
        {/* California Climate Action Tool Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 className="tool-title">California Climate Action Planning Tool</h2>
            <div className="tool-content">
              {/* Left Column - Tool Description */}
              <div className="tool-description">
                <h3>Climate Action Data for California Communities</h3>
                <p>
                  Access comprehensive climate indicators, emissions data, and sustainability metrics 
                  for California cities and counties to support evidence-based climate action planning.
                </p>
                
                <div className="tool-features">
                  <h4>Data Categories:</h4>
                  <ul>
                    <li><strong>Emissions:</strong> Sector-Based and Consumption-Based GHG Inventories</li>
                    <li><strong>Transportation:</strong> VMT trends, EV adoption, and transit metrics</li>
                    <li><strong>Energy:</strong> Energy consumption, built environment, solar photovoltaics</li>
                    <li><strong>Demographics:</strong> Population, health, and equity indicators</li>
                    <li><strong>Land Use:</strong> Waste management and environmental data</li>
                  </ul>
                </div>
              </div>

              {/* Right Column - Location Selector & Stats */}
              <div className="tool-action">
                <div className={`location-selector-wrapper ${isScrolled ? 'scrolled' : ''}`}>
                  <h4>Explore a Location</h4>
                  <LocationSelector 
                    onLocationSelect={onLocationSelect}
                    placeholder="Search for any California city or county"
                    className="main-location-selector"
                  />
                  
                  {/* Data Coverage Stats */}
                {!loading && (
                  <div className="locations-summary">
                    <h4>Data Coverage</h4>
                    <div className="summary-stats">
                      <div className="stat-item">
                        <span className="stat-number">{cities.length}</span>
                        <span className="stat-label">Cities</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{counties.length}</span>
                        <span className="stat-label">Counties</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{availableLocations.length}</span>
                        <span className="stat-label">Total</span>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CoolClimate Calculator Section - White Background */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 className="tool-title">CoolClimate Calculator</h2>
            <div className="tool-content">
              {/* Left Column - Tool Screenshot */}
              <div className="tool-screenshot">
                <div className="screenshot-placeholder">
                  <div className="placeholder-content">
                    <div className="placeholder-icon"></div>
                    <div className="placeholder-title">CoolClimate Calculator</div>
                    <div className="placeholder-subtitle">Carbon Footprint Tool</div>
                    <div className="placeholder-features">
                      <div className="feature-item">Home Energy</div>
                      <div className="feature-item">Transportation</div>
                      <div className="feature-item">Food & Shopping</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Tool Description & Action */}
              <div className="tool-description">
                <h3>Calculate Your Household Carbon Footprint</h3>
                <p>
                  Use the CoolClimate Calculator to assess your household's environmental impact across 
                  travel, home energy, food consumption, and shopping habits.
                </p>
                
                <div className="tool-features">
                  <h4>What You'll Get:</h4>
                  <ul>
                    <li><strong>Personalized Assessment:</strong> Calculate your household carbon footprint</li>
                    <li><strong>Benchmark Comparison:</strong> Compare your results to similar households</li>
                    <li><strong>Action Planning:</strong> Create a personalized climate action plan</li>
                    <li><strong>Category Breakdown:</strong> See emissions by travel, home, food, goods, and services</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="/coolclimate" className="tool-go-button">
                    Go to Calculator →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EV Equity Roadmap Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 className="tool-title">EV Equity Roadmap</h2>
            <div className="tool-content">
              {/* Left Column - Tool Screenshot */}
              <div className="tool-screenshot">
                <img 
                  src="/images/ev_map.png" 
                  alt="EV Equity Roadmap Tool Screenshot" 
                  className="tool-image"
                />
              </div>

              {/* Right Column - Tool Description & Action */}
              <div className="tool-description">
                <h3>Identify High-Priority EV Charging Sites</h3>
                <p>
                  This tool is intended for free use by local governments and stakeholders to identify 
                  high-priority, high-feasibility sites for investment in electric vehicle charging and 
                  associated mobility infrastructure.
                </p>
                
                <div className="tool-features">
                  <h4>About This Tool:</h4>
                  <ul>
                    <li><strong>Joint Project:</strong> UC Berkeley's Energy and Resources Group and Center for Law, Energy & the Environment (CLEE)</li>
                    <li><strong>EV Equity Initiative:</strong> Part of CLEE's broader equity-focused program</li>
                    <li><strong>Public Data:</strong> All data acquired from public sources except as noted</li>
                    <li><strong>Funding:</strong> Initial funding provided by UC Berkeley Institute for Transportation Studies</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="https://evmap.climateplans.org/" target="_blank" rel="noopener noreferrer" className="tool-go-button">
                    Go to EV Roadmap →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* State of Change Newsletter Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 className="tool-title">State of Change Newsletter</h2>
            <div className="tool-content">
              {/* Left Column - Newsletter Description */}
              <div className="tool-description">
                <h3>Climate Equity Reporting from UC Berkeley</h3>
                <p>
                  Stay informed with the latest climate equity reporting from the UC Berkeley Climate Journalism Lab. 
                  Our monthly newsletter covers California's climate action, environmental justice, and the communities 
                  most affected by climate change.
                </p>
                
                <div className="tool-features">
                  <h4>Recent Coverage:</h4>
                  <ul>
                    <li><strong>Manufacturing & Energy:</strong> Electric coffee roasting, biomass energy, and low-carbon manufacturing</li>
                    <li><strong>Transportation:</strong> Electric school buses, getting people out of cars, and transit-friendly housing</li>
                    <li><strong>Climate Justice:</strong> Sea level rise equity, polluter accountability, and environmental justice</li>
                    <li><strong>Renewable Energy:</strong> Solar panels on farms and canals, renewable energy policy impacts</li>
                    <li><strong>Policy & Politics:</strong> CEQA updates, climate bond impacts, and federal policy changes</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="https://climateequity.substack.com/" target="_blank" rel="noopener noreferrer" className="tool-go-button">
                    Subscribe to Newsletter →
                  </a>
                  <p style={{ marginTop: '16px', fontSize: '0.9rem', color: '#64748b' }}>
                    Free monthly newsletter from the Climate Equity Reporting Project
                  </p>
                </div>
              </div>

              {/* Right Column - Newsletter Preview */}
              <div className="tool-screenshot">
                <div className="newsletter-preview">
                  <div className="newsletter-header">
                    <div className="newsletter-branding">
                      <span className="newsletter-brand">UC Berkeley Journalism</span>
                      <div className="newsletter-divider"></div>
                    </div>
                    <h4 className="newsletter-title">STATE OF CHANGE</h4>
                    <p className="newsletter-subtitle">A NEWSLETTER FROM THE CLIMATE EQUITY REPORTING PROJECT</p>
                  </div>
                  
                  <div className="newsletter-content">
                    <div className="newsletter-article">
                      <h5>Electric Coffee Roasting</h5>
                      <p>Exploring the path to low-carbon manufacturing through electric coffee roasting and other industrial electrification solutions.</p>
                    </div>
                    
                    <div className="newsletter-article">
                      <h5>Sea Level Rise Equity</h5>
                      <p>How do we prepare for sea level rise with equity in mind? Plus updates on the Big Beautiful Bill's climate impact.</p>
                    </div>
                    
                    <div className="newsletter-article">
                      <h5>Solar on Farms & Canals</h5>
                      <p>Farmers navigating renewable energy investments and the first solar tunnel going up over California's canals.</p>
                    </div>
                  </div>
                  
                  <div className="newsletter-footer">
                    <span className="newsletter-tag">Climate Equity • UC Berkeley • Free Subscription</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Survey Section - White Background */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 className="tool-title">Help Us Improve These Tools</h2>
            <div className="tool-content">
              {/* Left Column - Survey Description */}
              <div className="tool-description">
                <h3>UCB CAP Tools Local Government Survey</h3>
                <p>
                  We're developing greenhouse gas (GHG) inventories and policy recommendations for local jurisdictions statewide. 
                  Your input will help us create tools that truly meet the needs of California communities.
                </p>
                
                <div className="tool-features">
                  <h4>What We're Looking For:</h4>
                  <ul>
                    <li><strong>Current Needs:</strong> What challenges does your jurisdiction face?</li>
                    <li><strong>Tool Gaps:</strong> What's missing from existing climate planning tools?</li>
                    <li><strong>User Experience:</strong> How can we make these tools more useful?</li>
                    <li><strong>Data Priorities:</strong> What information is most valuable for your work?</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="https://www.surveymonkey.com/r/MN7CMLS" target="_blank" rel="noopener noreferrer" className="tool-go-button">
                    Take the Survey →
                  </a>
                  <p style={{ marginTop: '16px', fontSize: '0.9rem', color: '#64748b' }}>
                    Prefer a 30-60 minute interview? Email Miya Kitahara at miya@stopwaste.org
                  </p>
                </div>
              </div>

              {/* Right Column - QR Code */}
              <div className="tool-screenshot">
                <div className="survey-qr-container">
                  <img 
                    src="/images/survey_qr.png" 
                    alt="Survey QR Code" 
                    className="survey-qr-code"
                  />
                  <div className="qr-label">
                    <strong>Scan to take the survey</strong>
                    <br />
                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                      UCB CAP Tools Local Government Survey
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stay Updated Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <div className="signup-link-section" style={{ 
              maxWidth: '600px',
              margin: '0 auto',
              padding: '20px', 
              backgroundColor: '#f0f9ff', 
              border: '2px solid #0ea5e9', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <h4 style={{ marginTop: '0', marginBottom: '12px', color: '#0369a1', fontSize: '1.2rem' }}>
                📧 Stay Updated
              </h4>
              <p style={{ marginBottom: '16px', color: '#334155', fontSize: '0.95rem' }}>
                Sign up to receive updates and news about this website
              </p>
              
              <div style={{ 
                marginBottom: '16px', 
                textAlign: 'left',
                backgroundColor: 'white',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  gap: '8px'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptCommunications}
                    onChange={(e) => setAcceptCommunications(e.target.checked)}
                    style={{
                      marginTop: '4px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span style={{ 
                    fontSize: '0.9rem', 
                    color: '#334155',
                    lineHeight: '1.5'
                  }}>
                    I agree to receive communications from ClimatePlans. We will not share contact information with third parties.
                  </span>
                </label>
              </div>
              
              <a 
                href="https://forms.office.com/pages/responsepage.aspx?id=2HwTd62DSUaIGt-8OLdWaH-Lquxf1HpKjg7IJiaZCVNUMjNIT0hDU004RFdCNjFQUTdaUTNZSzRBNC4u&route=shorturl"
                target="_blank"
                rel="noopener noreferrer"
                className="tool-go-button"
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  backgroundColor: acceptCommunications ? '#0ea5e9' : '#94a3b8',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                  cursor: acceptCommunications ? 'pointer' : 'not-allowed',
                  pointerEvents: acceptCommunications ? 'auto' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (acceptCommunications) {
                    e.currentTarget.style.backgroundColor = '#0284c7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (acceptCommunications) {
                    e.currentTarget.style.backgroundColor = '#0ea5e9';
                  }
                }}
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }

  // Dashboard view - show when isLandingPage is false
  // If no location selected, show message to select location
  if (!selectedLocation) {
    return (
      <div className="overview-page">
        <PageHeader 
          title="Overview"
          subtitle="Please select a location to view the overview dashboard."
          variant="overview"
        />
        <div className="overview-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '30px' }}>
            Select a location from the header to view climate action data and metrics.
          </p>
        </div>
      </div>
    );
  }

  // Dashboard with location selected
  return (
    <div className="overview-page">
      <PageHeader 
        title={`Overview - ${selectedLocation}`}
        subtitle="Sample of Climate Action data and figures"
        variant="overview"
      />
      
      <div className="overview-content" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Section 1: Burden/Benefits Charts */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
              {renderSampleTitle(
                `Livability & Climate Equity Index${burdenPercentile !== null && benefitsPercentile !== null ? `: ${((benefitsPercentile / 100 + (1 - burdenPercentile / 100)) / 2 * 100).toFixed(1)}` : ''}`,
                false
              )}
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '20px',
              marginBottom: '20px'
            }}>
              <div style={{ border: '2px solid #dc2626', borderRadius: '8px', padding: '20px', background: 'white' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '4px', textAlign: 'center', color: '#dc2626' }}>
                  Burdens
                </h3>
                <div style={{ fontSize: '1rem', textAlign: 'center', color: '#6b7280', marginBottom: '20px' }}>
                  (percentiles)
                </div>
                <BurdenBenefitsChart 
                  type="burdens" 
                  housingBurdenPercentile={housingBurdenPercentile}
                  onPercentileCalculated={(percentile) => setBurdenPercentile(percentile)}
                />
              </div>
              <div style={{ border: '2px solid #10b981', borderRadius: '8px', padding: '20px', background: 'white' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '4px', textAlign: 'center', color: '#10b981' }}>
                  Benefits
                </h3>
                <div style={{ fontSize: '1rem', textAlign: 'center', color: '#6b7280', marginBottom: '20px' }}>
                  (percentiles)
                </div>
                <BurdenBenefitsChart 
                  type="benefits"
                  onPercentileCalculated={(percentile) => setBenefitsPercentile(percentile)}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Section 2: Buildings */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
              {renderSampleTitle('Buildings & Energy')}
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '20px',
              marginBottom: '20px'
            }}>
            <div 
              onClick={() => handleNavigateWithCheck('/buildings')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '300px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Electricity Consumption{elecChangeSince2005 !== null ? `: ${elecChangeSince2005 > 0 ? '+' : ''}${elecChangeSince2005.toFixed(1)}% ${elecChangeSince2005 > 0 ? 'Increase' : 'Decrease'}` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1 }}>
                {elecStacked ? (
                  <ChartRendererSupabase 
                    chartData={elecStacked.chartData} 
                    chartOptions={elecStacked.chartOptions} 
                    height="250px" 
                  location={selectedLocation} 
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    <p>Loading Electricity data...</p>
                  </div>
                )}
              </div>
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/buildings')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '300px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Natural Gas Consumption{gasChangeSince2005 !== null ? `: ${gasChangeSince2005 > 0 ? '+' : ''}${gasChangeSince2005.toFixed(1)}% ${gasChangeSince2005 > 0 ? 'Increase' : 'Decrease'}` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1 }}>
                {gasStacked ? (
                  <ChartRendererSupabase 
                    chartData={gasStacked.chartData} 
                    chartOptions={gasStacked.chartOptions} 
                    height="250px" 
                  location={selectedLocation} 
                />
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    <p>Loading Natural Gas data...</p>
                  </div>
                )}
              </div>
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/buildings')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '300px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Cumulative Solar Capacity{cumulativeSolarCapacity !== null ? `: ${cumulativeSolarCapacity.toFixed(1)} MW` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                {selectedGeofips ? (
                  <div style={{ height: '250px', width: '100%' }}>
                    <CumulativeSolarCapacityChart 
                  location={selectedLocation} 
                      geofips={selectedGeofips}
                  embedOnly={true}
                />
              </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    <p>Loading Solar PV data...</p>
                  </div>
                )}
              </div>
            </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => handleNavigateWithCheck('/buildings')}
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                More Buildings & Energy Figures →
              </button>
            </div>
          </div>
        </div>

        {/* Section 3: Transportation */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
              {renderSampleTitle('Transportation')}
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '15px',
              marginBottom: '20px'
            }}>
            <div 
              onClick={() => handleNavigateWithCheck('/transportation')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '12px', 
                background: 'white', 
                minHeight: '420px',
                height: '420px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                Vehicle Miles{vmtChangeSince2005 !== null ? `: ${vmtChangeSince2005 > 0 ? '+' : ''}${vmtChangeSince2005.toFixed(1)}% ${vmtChangeSince2005 > 0 ? 'Increase' : 'Decrease'} since 2005` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', width: '100%', height: '100%', position: 'relative' }}>
                <VMTChart 
                    location={selectedLocation} 
                  geofips={selectedGeofips || null}
                  chartView="total"
                  backcastingMethod="Combined"
                  emfac2005_2014Method="Combined"
                  emfacFleetVersion="2025"
                  embedOnly={true}
                  onMetricsCalculated={(metrics) => setVmtChangeSince2005(metrics.changeSince2005)}
                  onDataCalculated={(data) => {
                    // Only process if we have valid data
                    if (!data || data.length === 0) {
                      console.log('⚠️ Overview: VMT Chart onDataCalculated called with empty data, skipping');
                      return;
                    }
                    
                    // Check if we've already processed VMT chart data for this location
                    // If the location changed, the refs will be reset by the useEffect, so this check is safe
                    if (vmtChartDataLoadedRef.current && ghgInventoryLoadedForLocationRef.current === selectedLocation) {
                      console.log('⏸️ Overview: VMT Chart data already processed for', selectedLocation, '- skipping duplicate onDataCalculated call');
                      return;
                    }
                    
                    // If location changed but refs haven't been reset yet, reset them now
                    if (ghgInventoryLoadedForLocationRef.current && ghgInventoryLoadedForLocationRef.current !== selectedLocation) {
                      console.log('🔄 Overview: Location changed from', ghgInventoryLoadedForLocationRef.current, 'to', selectedLocation, '- resetting refs');
                      vmtChartDataLoadedRef.current = false;
                      ghgInventoryLoadedForLocationRef.current = null;
                      ghgInventoryLoadingRef.current = false;
                    }
                    
                    console.log('✅ Overview: VMT Chart on Overview page has loaded data:', data?.length || 0, 'records');
                    console.log('✅ Overview: VMT data years:', data.map(d => d.year));
                    
                    // Store VMT data in global service (same as Transportation page does)
                    // This ensures GHG Inventory can access the backcasted VMT data including years 2005-2014
                    setVMTDataFromTransportation(data.map(d => ({
                      year: d.year,
                      vmt: d.vmt || 0,
                      source: 'overview_vmt_chart',
                      method_note: 'VMT data from Overview page VMT chart - includes backcasted years 2005-2014'
                    })));
                    
                    // Also set legacy window variable for backward compatibility
                    if (typeof window !== 'undefined') {
                      window.vmtChartData = data.map((d: any) => ({
                        year: d.year,
                        vmt: d.vmt || 0
                      }));
                      console.log('✅ Overview: VMT data set in global service and window variable for GHG inventory');
                    }
                    
                    // Only update state flags once per location
                    if (!vmtChartDataLoadedRef.current) {
                      setOverviewVMTChartLoaded(true);
                      setVmtDataAvailable(true);
                      setVmtDataReady(true);
                      vmtChartDataLoadedRef.current = true;
                    }
                    
                    // Now that VMT chart has loaded, load GHG Inventory (only once per location)
                    if (selectedLocation && 
                        !ghgInventoryLoadingRef.current && 
                        ghgInventoryLoadedForLocationRef.current !== selectedLocation) {
                      ghgInventoryLoadingRef.current = true;
                      console.log('🔄 Overview: Loading GHG Inventory now that VMT chart has loaded for', selectedLocation);
                      fetchGHGInventoryData(selectedLocation)
                        .then((ghgResult) => {
                          setGhgInventoryData(ghgResult);
                          ghgInventoryLoadedForLocationRef.current = selectedLocation; // Mark as loaded for this location
                          console.log('✅ Overview: Sector-based GHG Inventory data loaded for', selectedLocation);
                          ghgInventoryLoadingRef.current = false;
                        })
                        .catch((error) => {
                          console.warn('Overview: Sector-based GHG Inventory load failed:', error);
                          setGhgInventoryData(null);
                          ghgInventoryLoadingRef.current = false;
                          // Don't set ghgInventoryLoadedForLocationRef on error, so we can retry
                        });
                    } else if (!selectedLocation) {
                      console.warn('Overview: Cannot load GHG Inventory - selectedLocation is not available');
                    } else if (ghgInventoryLoadingRef.current) {
                      console.log('⏸️ Overview: GHG Inventory already loading, skipping duplicate call');
                    } else if (ghgInventoryLoadedForLocationRef.current === selectedLocation) {
                      console.log('⏸️ Overview: GHG Inventory already loaded for', selectedLocation, '- skipping duplicate call');
                    }
                  }}
                />
              </div>
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/transportation')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '12px', 
                background: 'white', 
                minHeight: '420px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                Mode Share{privateVehiclePercent !== null ? `: ${privateVehiclePercent.toFixed(1)}% Private Vehicle` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1 }}>
                <TransportationModeShare 
                    location={selectedLocation} 
                  geofips={selectedGeofips || null} 
                  embedOnly={true}
                  onModeShareCalculated={(data) => setPrivateVehiclePercent(data.privateAuto)}
                  />
              </div>
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/transportation')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '12px', 
                background: 'white', 
                minHeight: '420px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                EV Fleet Adoption{evPercent !== null ? `: ${evPercent.toFixed(1)}% of light-duty vehicles` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1 }}>
                <EVFleetAdoption 
                    location={selectedLocation} 
                  geofips={selectedGeofips || null} 
                  embedOnly={true}
                  onEvPercentCalculated={(percent) => setEvPercent(percent)}
                  />
              </div>
            </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => handleNavigateWithCheck('/transportation')}
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                More Transportation Figures →
              </button>
            </div>
          </div>
        </div>

        {/* Section 4: Waste+ */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
              {renderSampleTitle('Waste +')}
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '20px',
              marginBottom: '20px'
            }}>
            <div 
              onClick={() => handleNavigateWithCheck('/other')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '500px',
                height: '500px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Waste Tonnage{wasteChangeSince2005 !== null ? `: ${wasteChangeSince2005 > 0 ? '+' : ''}${wasteChangeSince2005.toFixed(1)}% ${wasteChangeSince2005 > 0 ? 'Increase' : 'Decrease'} since 2005` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                <WasteTonnageChart 
                  location={selectedLocation}
                  embedOnly={true}
                  onWasteChangeCalculated={(change) => setWasteChangeSince2005(change)}
                />
              </div>
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/other')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '500px',
                height: '500px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Landfill Emissions{landfillEmissions2023 !== null ? `: ${landfillEmissions2023.toLocaleString()} tons CO₂e (2023)` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                <LandfillEmissionsStackedChart 
                  location={selectedLocation} 
                  embedOnly={true}
                  onEmissionsCalculated={(emissions) => setLandfillEmissions2023(emissions)}
                />
              </div>
            </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => handleNavigateWithCheck('/other')}
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                More Waste+ Figures →
              </button>
            </div>
          </div>
        </div>

        {/* Section 5: People & Equity - Charts Only */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
              {renderSampleTitle('People & Equity')}
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '20px',
              marginBottom: '20px'
            }}>
            <div 
              onClick={() => handleNavigateWithCheck('/people')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '440px',
                height: '440px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Income by Race{incomeDifferenceRatio !== null ? ` (${incomeDifferenceRatio.toFixed(1)}x difference)` : ''}
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                <IncomeByRaceBar key={selectedLocation} location={selectedLocation} embedOnly={true} onIncomeDifference={setIncomeDifferenceRatio} />
              </div>
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/people')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '440px',
                height: '440px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Households Spending 35%+ of Income on Housing
              </h4>
              <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                <HousingBurdenSection location={selectedLocation} geofips={selectedGeofips ? String(selectedGeofips) : null} embedOnly={true} />
              </div>
            </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => handleNavigateWithCheck('/people')}
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                More People & Equity Figures →
              </button>
            </div>
          </div>
        </div>

        {/* Section 6: GHG Inventory */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
              {renderSampleTitle('GHG Inventory')}
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '20px',
              marginBottom: '20px'
            }}>
            <div 
              onClick={() => handleNavigateWithCheck('/ghg')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '400px', 
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Sector-Based GHG Inventory
              </h4>
              {overviewVMTChartLoaded && vmtDataReady && vmtDataAvailable && ghgInventoryData ? (
                <div style={{ height: '370px', pointerEvents: 'none', flex: 1, overflow: 'hidden' }}>
                  <SectorBasedGHGInventory 
                    location={selectedLocation} 
                    chartType={ghgChartType} 
                    setChartType={setGhgChartType} 
                    ghgData={ghgInventoryData} 
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', flex: 1 }}>
                  <p>{overviewVMTChartLoaded ? 'Loading Sector-Based GHG Inventory...' : 'Loading VMT chart data...'}</p>
                </div>
              )}
            </div>
            <div 
              onClick={() => handleNavigateWithCheck('/ghg')}
              style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '15px', 
                background: 'white', 
                minHeight: '400px', 
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                Consumption-Based GHG Inventory
              </h4>
              {consumptionGHGData ? (
                <div style={{ height: '370px', pointerEvents: 'none', flex: 1, overflow: 'hidden' }}>
                  <ConsumptionBasedGHGInventory 
                    location={selectedLocation} 
                    chartType={consumptionChartType} 
                    setChartType={setConsumptionChartType} 
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', flex: 1 }}>
                  <p>Loading Consumption-Based GHG Inventory...</p>
                </div>
              )}
            </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => handleNavigateWithCheck('/ghg')}
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                More GHG Inventory Figures →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Download Modal */}
      <DataDownloadModal
        isOpen={showDataDownload}
        onClose={() => setShowDataDownload(false)}
        location={selectedLocation || ''}
        geofips={selectedGeofips}
      />
    </div>
  );
};
